import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTour } from '@reactour/tour';
import { LayoutDashboard, Sliders, ArrowRight, ListTodo, Bug, Terminal, Map } from 'lucide-react';
import { CurriculumHeader } from '../components/CurriculumHeader';
import { useLywUserTodos } from '../hooks/useLywUserTodos';
import { useLywTourStorageKey } from '../components/lywOnboardingTourSteps';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { TodoPanel } from '../components/TodoPanel';
import { useSessionDebugMode } from '../hooks/useSessionDebugMode';
import {
  disableInlineConsole,
  enableInlineConsole,
  INLINE_CONSOLE_CHANGED_EVENT,
  isInlineConsoleEnabled,
} from '../../modules/inline-console';

export function DashboardPage() {
  const navigate = useNavigate();
  const { setIsOpen, setCurrentStep } = useTour();
  const tourStorageKey = useLywTourStorageKey();
  const todos = useLywUserTodos();
  const sessionDebugMode = useSessionDebugMode();

  const [ilcOpen, setIlcOpen] = useState(() =>
    typeof window !== 'undefined' ? isInlineConsoleEnabled() : false
  );

  useEffect(() => {
    const handler = (e: Event) => {
      const open = (e as CustomEvent<{ open?: boolean }>).detail?.open;
      setIlcOpen(open ?? isInlineConsoleEnabled());
    };
    window.addEventListener(INLINE_CONSOLE_CHANGED_EVENT, handler);
    return () => window.removeEventListener(INLINE_CONSOLE_CHANGED_EVENT, handler);
  }, []);

  const toggleInlineConsole = () => {
    if (isInlineConsoleEnabled()) {
      disableInlineConsole();
    } else {
      enableInlineConsole();
    }
  };

  const handleTakeTourAgain = () => {
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(tourStorageKey);
    }
    setCurrentStep(0);
    setIsOpen(true);
  };

  return (
    <div className="p-4 sm:p-6">
      <CurriculumHeader
        showTabs={false}
        showStudentDropdown={false}
        title="Dashboard"
        icon={LayoutDashboard}
      />

      <p className="text-gray-600 mb-6">
        Your home base for setup tasks and account settings
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* To Dos tile - renders todos inline */}
        <Card className="border-2 border-gray-200">
          <CardHeader>
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-orange-50">
                <ListTodo className="text-orange-600" size={20} />
              </div>
              <CardTitle className="text-lg font-semibold text-gray-900">To Do</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <TodoPanel todos={todos} />
          </CardContent>
        </Card>

        {/* Control Panel tile - large, spans 2 columns on desktop */}
        <Card
          className="lg:col-span-2 hover:shadow-lg transition-all duration-200 cursor-pointer border-2 border-action-blue/30 hover:scale-[1.02]"
          onClick={() => navigate('/app/settings')}
        >
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="p-3 rounded-lg bg-action-blue/10">
                <Sliders className="text-action-blue" size={24} />
              </div>
              <ArrowRight className="text-gray-400" size={20} />
            </div>
            <CardTitle className="text-xl font-semibold text-gray-900 mt-4">
              Control Panel
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 text-sm leading-relaxed">
              Profile, billing, users, invitations, and education system settings.
            </p>
            <div className="mt-4 pt-4 border-t border-gray-100">
              <span className="text-xs text-gray-500 uppercase tracking-wider font-medium">
                Click to open settings
              </span>
            </div>
          </CardContent>
        </Card>

        {sessionDebugMode && (
          <Card className="lg:col-span-3 border-2 border-amber-200 bg-amber-50/40">
            <CardHeader>
              <div className="flex items-center gap-2">
                <div className="p-2 rounded-lg bg-amber-100">
                  <Bug className="text-amber-800" size={20} />
                </div>
                <CardTitle className="text-lg font-semibold text-gray-900">Debug Mode</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-gray-700">
                Session debug mode is on (from Help chat). Use inline console on devices without
                DevTools—logs and network activity appear in a panel at the bottom of the page.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="border-amber-300 text-amber-900 hover:bg-amber-100/80"
                  aria-label={ilcOpen ? 'Close inline console' : 'Open inline console'}
                  onClick={toggleInlineConsole}
                >
                  <Terminal className="h-4 w-4 mr-2" aria-hidden />
                  {ilcOpen ? 'Close inline console (ILC)' : 'Open inline console (ILC)'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="text-[#1a5172] border-[#1a5172] hover:bg-[#1a5172]/5"
                  aria-label="Take the tour again"
                  onClick={handleTakeTourAgain}
                >
                  <Map className="h-4 w-4 mr-2" aria-hidden />
                  Take the Tour Again
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
